import React from 'react'
import { Box, IconButton, Popover, Typography } from '@mui/material'
import Close from '@mui/icons-material/Close'
import type { BffCrmPersonComplexitySegmentationRetrieveResponse } from '@fireflyhealth/api'
import { GREY, NAVY, YELLOW } from '~/shared/lib/theme'

const BAR_GRADIENT = 'linear-gradient(to right, #4ade80, #fbbf24, #f87171)'
const MARKER_COLOR = NAVY[400]

type PredictedSegment = 'high' | 'medium' | 'low'

interface ComplexityPopoverProps {
  anchorEl: HTMLElement | null
  onClose: () => void
  segmentation: BffCrmPersonComplexitySegmentationRetrieveResponse
}

const TIER_DESCRIPTIONS: Record<PredictedSegment, string> = {
  high: 'High complexity members are predicted to have high healthcare utilization, often requiring intensive coordination and frequent intervention. The factors listed above represent the three specific clinical signals or historical data points that carried the most weight in pushing this patients prediction score into the high tier.',
  medium:
    'Medium complexity members demonstrate moderate anticipated healthcare utilization, often requiring regular monitoring or periodic care. The factors listed above represent the specific signals, utilization metrics, or historical data points that had the greatest impact driving the current score.',
  low: 'Low complexity members typically show patterns of minimal or stable healthcare utilization. The factors listed above represent the top three data points—which may include a lack of recent claims or medical history that are predictive of healthcare utilization influencing this current score.',
}

function getOrdinalSuffix(n: number): string {
  const mod100 = n % 100
  if (mod100 >= 11 && mod100 <= 13) return 'th'
  const mod10 = n % 10
  if (mod10 === 1) return 'st'
  if (mod10 === 2) return 'nd'
  if (mod10 === 3) return 'rd'
  return 'th'
}

const ComplexityPopover: React.FC<ComplexityPopoverProps> = ({
  anchorEl,
  onClose,
  segmentation,
}) => {
  const segment = segmentation.predicted_segment
  const percentile = Math.round(parseFloat(segmentation.complexity_score_percentile))
  const markerPosition = Math.max(0, Math.min(100, percentile))
  const ordinal = `${percentile}${getOrdinalSuffix(percentile)}`

  const factors = [
    { title: segmentation.top_1_feature_title, displayValue: segmentation.top_1_display_value },
    { title: segmentation.top_2_feature_title, displayValue: segmentation.top_2_display_value },
    { title: segmentation.top_3_feature_title, displayValue: segmentation.top_3_display_value },
  ].filter((factor): factor is { title: string; displayValue: string | null } => {
    return factor.title != null && factor.title !== ''
  })

  return (
    <Popover
      open={anchorEl != null}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      disableRestoreFocus
      slotProps={{ paper: { sx: { width: 420, p: 3, borderRadius: 2 } } }}
    >
      {/* Header: percentile title + close button */}
      <Box
        sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1.5 }}
      >
        <Typography variant="h6" sx={{ fontWeight: 700, color: GREY[900] }}>
          {ordinal} percentile
        </Typography>
        <IconButton
          size="small"
          onClick={onClose}
          aria-label="close complexity popover"
          sx={{ mt: -0.5, mr: -0.5 }}
        >
          <Close fontSize="small" />
        </IconButton>
      </Box>

      {/* Gradient bar with 0% / 100% labels */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
        <Typography variant="body2" sx={{ color: GREY[700], whiteSpace: 'nowrap' }}>
          0%
        </Typography>
        <Box sx={{ position: 'relative', flex: 1, height: 12, borderRadius: 6 }}>
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              borderRadius: 6,
              background: BAR_GRADIENT,
            }}
          />
          {/* Downward-pointing triangle marker */}
          <Box
            aria-label="complexity-percentile-marker"
            sx={{
              position: 'absolute',
              top: -9,
              left: `${markerPosition}%`,
              transform: 'translateX(-50%)',
              width: 0,
              height: 0,
              borderLeft: '7px solid transparent',
              borderRight: '7px solid transparent',
              borderTop: `10px solid ${MARKER_COLOR}`,
            }}
          />
        </Box>
        <Typography variant="body2" sx={{ color: GREY[700], whiteSpace: 'nowrap' }}>
          100%
        </Typography>
      </Box>

      {/* Description */}
      <Typography variant="body2" sx={{ color: GREY[800], mb: 2 }}>
        Patient&apos;s anticipated healthcare utilization is at the {ordinal} percentile of our
        member population.
      </Typography>

      {/* Contributing factors */}
      {factors.length > 0 && (
        <>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: GREY[900], mb: 1 }}>
            Top 3 contributing factors
          </Typography>
          <Box component="ul" sx={{ listStyleType: 'disc', pl: 2.5, m: 0, mb: 2 }}>
            {factors.map((factor, index) => (
              <Box key={index} component="li" sx={{ py: 0.25 }}>
                <Typography variant="body2" sx={{ color: GREY[800] }}>
                  {factor.title}
                  {factor.displayValue != null && factor.displayValue !== '' && (
                    <>
                      :{' '}
                      <Box component="span" sx={{ fontWeight: 700 }}>
                        {factor.displayValue}
                      </Box>
                    </>
                  )}
                </Typography>
              </Box>
            ))}
          </Box>
        </>
      )}

      {/* Tier description highlight box */}
      <Box sx={{ bgcolor: YELLOW[50], borderRadius: 1, p: 2 }}>
        <Typography variant="body2" sx={{ color: GREY[800] }}>
          {TIER_DESCRIPTIONS[segment]}
        </Typography>
      </Box>
    </Popover>
  )
}

export default ComplexityPopover
